/**
 * Иммутабельные типы MIDI.
 *
 * Внутреннее представление проекта: парсер (`parse.ts`) превращает
 * байты SMF в `Midi`, энкодер (`write.ts`) — обратно. Все
 * трансформации (`transformMidi`, `buildReport`, `buildPlaybackData`)
 * работают над этим типом без классов и без побочных эффектов.
 */

export const MIDI_VELOCITY_MAX = 127;
export const DRUM_CHANNEL = 9;
export const PROGRAM_CHANGE_CC = 0xc;

/**
 * 15 тональностей квинтового круга (signed -7..+7) с параллельными
 * мажорными и минорными обозначениями. Используется парсером и
 * энкодером KeySignature (FF 59).
 */
export const KEY_SIGNATURE_MAP = [
  { signed: -7, major: "Cb", minor: "Ab" },
  { signed: -6, major: "Gb", minor: "Eb" },
  { signed: -5, major: "Db", minor: "Bb" },
  { signed: -4, major: "Ab", minor: "F" },
  { signed: -3, major: "Eb", minor: "C" },
  { signed: -2, major: "Bb", minor: "G" },
  { signed: -1, major: "F",  minor: "D" },
  { signed:  0, major: "C",  minor: "A" },
  { signed: +1, major: "G",  minor: "E" },
  { signed: +2, major: "D",  minor: "B" },
  { signed: +3, major: "A",  minor: "F#" },
  { signed: +4, major: "E",  minor: "C#" },
  { signed: +5, major: "B",  minor: "G#" },
  { signed: +6, major: "F#", minor: "D#" },
  { signed: +7, major: "C#", minor: "A#" },
] as const;

/**
 * Устаревшая хроматическая таблица. Оставлена только для внутренней
 * совместимости; для KeySignature используйте `KEY_SIGNATURE_MAP`.
 */
export const KEY_SIGNATURE_KEYS = [
  "Cb",
  "Gb",
  "Db",
  "Ab",
  "Eb",
  "Bb",
  "F",
  "C",
  "G",
  "D",
  "A",
  "E",
  "B",
  "F#",
  "C#",
] as const;

export type MidiChannel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;

export type MidiInstrument = {
  family: string;
  number: number;
  name: string;
  percussion: boolean;
};

export type MidiNote = {
  midi: number;
  ticks: number;
  durationTicks: number;
  velocity: number;
  noteOffVelocity?: number;
  /**
   * Канал исходного канального события (заполняется парсером для каждой
   * ноты). В исходных `MidiTrack` совпадает с `track.channel`; нужен для
   * `toMultiTrackFormat` (`packages/midi-util/src/transform/pack.ts`),
   * который разбивает format-0 треки на per-channel подтреки. Если поле
   * отсутствует (например, при ручном конструировании в тестах),
   * `toMultiTrackFormat` использует `track.channel` для всех событий.
   */
  channel?: MidiChannel;
};

export type MidiControlChange = {
  ticks: number;
  number: number;
  value: number;
  channel?: MidiChannel;
};

export type MidiPitchBend = {
  ticks: number;
  value: number;
  channel?: MidiChannel;
};

/**
 * SysEx-блок в сыром виде (без декодирования payload). `isF0 = true`
 * означает F0-старт (полноценный SysEx), `isF0 = false` — escape (F7).
 */
export type MidiSysEx = {
  ticks: number;
  bytes: Uint8Array;
  isF0: boolean;
};

export type MidiTempo = {
  ticks: number;
  bpm: number;
};

export type MidiTimeSignature = {
  ticks: number;
  timeSignature: [number, number];
};

export type MidiKeySignature = {
  ticks: number;
  key: string;
  scale: "major" | "minor";
};

/**
 * Текстовые meta-события (trackName, copyright, text, lyrics, marker, cue).
 * Парсер сохраняет исходный SMF type как строку — encoder пишет тот же байт.
 */
export type MidiMetaEvent = {
  ticks: number;
  type: string;
  text?: string;
};

export type MidiProgramChange = {
  ticks: number;
  value: number;
  channel?: MidiChannel;
};

export type MidiUnknownMeta = {
  ticks: number;
  type: number;
  bytes: Uint8Array;
};

export type MidiTrack = {
  name: string;
  channel: MidiChannel;
  instrument: MidiInstrument;
  notes: ReadonlyArray<MidiNote>;
  controlChanges: ReadonlyMap<number, ReadonlyArray<MidiControlChange>>;
  programChanges: ReadonlyArray<MidiProgramChange>;
  pitchBends: ReadonlyArray<MidiPitchBend>;
  sysex: ReadonlyArray<MidiSysEx>;
  meta: ReadonlyArray<MidiMetaEvent>;
  unknownMeta: ReadonlyArray<MidiUnknownMeta>;
};

export type MidiHeader = {
  name: string;
  ppq: number;
  tempos: ReadonlyArray<MidiTempo>;
  timeSignatures: ReadonlyArray<MidiTimeSignature>;
  keySignatures: ReadonlyArray<MidiKeySignature>;
  meta: ReadonlyArray<MidiMetaEvent>;
  /**
   * Полная длительность в секундах, вычисленная парсером по последнему
   * тику треков и карте темпо-событий. После `transformMidi`
   * пересчитывается заново, чтобы `Report.duration` не расходился
   * с реальным файлом.
   */
  durationSeconds: number;
};

export type Midi = {
  format: 0 | 1 | 2;
  header: MidiHeader;
  tracks: ReadonlyArray<MidiTrack>;
};